import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth-context";
import api from "@/lib/api";
import {
  Sparkles,
  ScanLine,
  Palette,
  Gauge,
  Users,
  ArrowRight,
  TrendingUp,
  FileText,
  ListChecks,
  PenLine,
  GraduationCap,
  Wand2,
  MessageSquare,
  Languages,
  Presentation,
  ClipboardList,
  Calendar,
  Bell,
  Lightbulb,
  Building2,
  ChevronRight,
  CircleDot,
  Flame,
  Target,
  Globe,
  Eye,
  Copy as CopyIcon,
  Compass,
  UserCog,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Sparkline from "@/components/Sparkline";
import { Skeleton, SkeletonRow } from "@/components/Skeleton";
import InlineSuggestionPanel from "@/components/InlineSuggestionPanel";

/* --------------------------------------------------------------
   Quick AI Action shelf — one-click intents from any teacher state.
   Each chip routes to the appropriate generator with the right tool param.
-------------------------------------------------------------- */
const QUICK_ACTIONS = [
  { key: "tfa",         label: "Teach anything", icon: Wand2,         route: "/teach-from-anything",                   color: "tb-chip-lavender" },
  { key: "scan",        label: "Scan",         icon: ScanLine,      route: "/ocr",                                   color: "tb-chip-mint" },
  { key: "lesson",      label: "Lesson plan",  icon: PenLine,       route: "/teaching-assistant?tool=lesson_plan",   color: "tb-chip-lavender" },
  { key: "mcq",         label: "MCQs",         icon: ListChecks,    route: "/teaching-assistant?tool=mcq",           color: "tb-chip-sky" },
  { key: "worksheet",   label: "Worksheet",    icon: FileText,      route: "/teaching-assistant?tool=worksheet",     color: "tb-chip-butter" },
  { key: "homework",    label: "Homework",     icon: ClipboardList, route: "/teaching-assistant?tool=homework",      color: "tb-chip-rose" },
  { key: "rubric",      label: "Rubric",       icon: GraduationCap, route: "/teaching-assistant?tool=rubric",        color: "tb-chip-lavender" },
  { key: "ppt",         label: "Presentation", icon: Presentation,  route: "/content-studio",                        color: "tb-chip-mint" },
  { key: "translate",   label: "Translate",    icon: Languages,     route: "/teaching-assistant?tool=lesson_plan",   color: "tb-chip-sky" },
  { key: "parent",      label: "Parent comms", icon: MessageSquare, route: "/parent-comms",                          color: "tb-chip-rose" },
];

const ICON_BY_KEY = {
  sparkles: Sparkles,
  "list-checks": ListChecks,
  "wand-2": Wand2,
  "scan-line": ScanLine,
};

const ACTIVITY_META = {
  ocr:         { icon: ScanLine,    chip: "tb-chip-mint",     label: "Document OCR" },
  generation:  { icon: Sparkles,    chip: "tb-chip-lavender", label: "AI generation" },
  assessment:  { icon: Gauge,       chip: "tb-chip-sky",      label: "Graded" },
};

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const s = Math.floor(diff / 1000);
    if (s < 60) return "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return "";
  }
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [feed, setFeed] = useState(null);
  const [streak, setStreak] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSuggestion, setActiveSuggestion] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, f, st, sh] = await Promise.allSettled([
          api.get("/dashboard/stats"),
          api.get("/dashboard/feed"),
          api.get("/preferences/streak"),
          api.get("/me/sharing-summary"),
        ]);
        if (!alive) return;
        if (s.status === "fulfilled")  setStats(s.value.data);
        if (f.status === "fulfilled")  setFeed(f.value.data);
        if (st.status === "fulfilled") setStreak(st.value.data);
        if (sh.status === "fulfilled") setSharing(sh.value.data);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const firstName = (user?.name || "Teacher").split(" ")[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const productivity = stats?.productivity_score ?? 0;
  const sparkline = feed?.productivity_sparkline ?? [];
  const aiSuggestions = feed?.ai_suggestions ?? [];
  const recentOcr = feed?.recent_ocr ?? [];
  const recentLessons = feed?.recent_lessons ?? [];
  const activity = feed?.activity_timeline ?? [];
  const notices = feed?.institution_notices ?? [];
  const reminders = feed?.assignment_reminders ?? [];
  const todayClasses = feed?.today_classes ?? [];

  return (
    <Layout title="Dashboard">
      {/* ===== Greeting hero ===== */}
      <section className="relative" data-testid="dashboard-hero">
        <div className="absolute inset-0 tb-grid-bg opacity-60 pointer-events-none rounded-3xl" />
        <div className="relative flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground">
              {greeting}
            </p>
            <h1 className="mt-1.5 font-display text-3xl sm:text-4xl lg:text-[2.75rem] tracking-tight font-medium leading-[1.1]">
              Hi, {firstName}.
              <span className="block text-muted-foreground mt-1 text-xl sm:text-2xl lg:text-3xl font-normal">
                Ready to teach something extraordinary?
              </span>
            </h1>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button
              variant="outline"
              className="rounded-full h-10"
              onClick={() => navigate("/ocr")}
              data-testid="dashboard-cta-scan"
            >
              <ScanLine className="h-4 w-4 mr-2" /> Scan a document
            </Button>
            <Button
              className="rounded-full h-10"
              onClick={() => navigate("/teach-from-anything")}
              data-testid="dashboard-cta-tfa"
            >
              <Wand2 className="h-4 w-4 mr-2" /> Teach from anything
            </Button>
            {user?.role === "admin" && (
              <Button
                variant="ghost"
                className="rounded-full h-10 border border-primary/40 text-primary hover:bg-primary/10"
                onClick={() => navigate("/admin")}
                data-testid="dashboard-cta-admin"
              >
                <TrendingUp className="h-4 w-4 mr-2" /> Try Admin View
              </Button>
            )}
          </div>
        </div>

        {/* Streak + weekly goal strip */}
        <StreakStrip streak={streak} sharing={sharing} navigate={navigate} loading={loading} />
      </section>

      {/* ===== Quick AI action shelf ===== */}
      <section className="mt-6" data-testid="quick-action-shelf">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground inline-flex items-center gap-2">
            <Wand2 className="h-3 w-3" /> Quick actions
          </p>
        </div>
        <div className="flex flex-wrap gap-2 pb-1 -mx-1 px-1 tb-stagger">
          {QUICK_ACTIONS.map((a) => {
            const Icon = a.icon;
            return (
              <button
                key={a.key}
                onClick={() => navigate(a.route)}
                className="tb-action-chip"
                data-testid={`quick-action-${a.key}`}
              >
                <span className={`inline-grid place-items-center h-6 w-6 rounded-full ${a.color}`}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                {a.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* ===== Career & Identity row ===== */}
      <CareerIdentityRow navigate={navigate} />

      {/* ===== Main bento grid ===== */}
      <section className="mt-6 grid grid-cols-12 gap-4 lg:gap-5">
        {/* Today panel — col-span 8 / mobile full */}
        <TodayPanel
          loading={loading}
          classes={todayClasses}
          reminders={reminders}
          onOpen={() => navigate("/attendance")}
        />

        {/* Productivity score + sparkline — col-span 4 */}
        <ProductivityCard
          loading={loading}
          score={productivity}
          sparkline={sparkline}
          thisWeek={stats?.this_week}
        />

        {/* AI Suggestions — col-span 4 */}
        <AISuggestionsCard
          suggestions={aiSuggestions}
          loading={loading}
          onActionClick={(s) => {
            if (s.action === "open_teaching") {
              navigate("/teaching-assistant");
            } else {
              // Run in-place via InlineSuggestionPanel (no navigation)
              setActiveSuggestion(s);
            }
          }}
        />

        {/* Activity timeline — col-span 4 row-span 2 */}
        <ActivityTimelineCard
          loading={loading}
          items={activity}
        />

        {/* Recent OCR uploads — col-span 4 */}
        <RecentOcrCard
          loading={loading}
          items={recentOcr}
          onOpen={() => navigate("/knowledge-hub")}
          onItemClick={() => navigate("/knowledge-hub")}
        />

        {/* Recent lessons — col-span 8 */}
        <RecentLessonsCard
          loading={loading}
          items={recentLessons}
          onOpen={() => navigate("/teaching-assistant")}
        />

        {/* Institution notices — col-span 4 */}
        <InstitutionNoticesCard
          loading={loading}
          items={notices}
          isInInstitution={!!user?.institution_id}
        />

        {/* Stats row — full width */}
        <StatsRow stats={stats} loading={loading} />
      </section>

      <InlineSuggestionPanel
        suggestion={activeSuggestion}
        onClose={() => setActiveSuggestion(null)}
      />
    </Layout>
  );
}

/* ============================================================
   Sub-components
   ============================================================ */

function TodayPanel({ loading, classes, reminders, onOpen }) {
  const hasContent = (classes?.length || 0) + (reminders?.length || 0) > 0;
  return (
    <div
      className="col-span-12 lg:col-span-8 tb-card p-6 sm:p-7 tb-fade-up"
      data-testid="today-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground inline-flex items-center gap-2">
            <Calendar className="h-3 w-3" /> Today
          </p>
          <h2 className="mt-2 font-display text-xl sm:text-2xl tracking-tight font-medium">
            Your day at a glance
          </h2>
        </div>
        <Button size="sm" variant="ghost" className="rounded-full" onClick={onOpen} data-testid="today-open">
          Open <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
        </Button>
      </div>

      {loading ? (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => <SkeletonRow key={i} />)}
        </div>
      ) : hasContent ? (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {classes?.map((c, idx) => (
            <div key={c.class_id || c._id || `class-${idx}`} className="rounded-2xl bg-muted/50 p-4 border border-border/60 flex items-start gap-3">
              <span className="tb-chip-mint h-9 w-9 rounded-xl grid place-items-center shrink-0">
                <Users className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{c.name || c.title || "Class"}</p>
                <p className="text-[11px] text-muted-foreground truncate">{c.subject || c.grade || "Untitled"}</p>
              </div>
            </div>
          ))}
          {reminders?.map((r, idx) => (
            <div key={r.assessment_id || r.record_id || `reminder-${idx}`} className="rounded-2xl bg-muted/50 p-4 border border-border/60 flex items-start gap-3">
              <span className="tb-chip-rose h-9 w-9 rounded-xl grid place-items-center shrink-0">
                <Bell className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{r.question_title || r.title || "Pending grading"}</p>
                <p className="text-[11px] text-muted-foreground truncate">Awaiting review</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyBlock
          icon={Calendar}
          title="A fresh canvas"
          hint="Once you create classes or assignments, today's schedule lands here."
          actionLabel="Set up your first class"
          onAction={onOpen}
        />
      )}
    </div>
  );
}

function ProductivityCard({ loading, score, sparkline, thisWeek }) {
  return (
    <div
      className="col-span-12 sm:col-span-6 lg:col-span-4 tb-card p-6 tb-fade-up"
      data-testid="productivity-card"
    >
      <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground inline-flex items-center gap-2">
        <TrendingUp className="h-3 w-3" /> Productivity
      </p>
      {loading ? (
        <Skeleton className="mt-3 h-12 w-32" />
      ) : (
        <div className="mt-3 flex items-baseline gap-2">
          <p className="font-display text-5xl font-medium tracking-tight">{score}</p>
          <p className="text-xs text-muted-foreground">/ 100</p>
        </div>
      )}
      <div className="mt-4">
        <Sparkline data={sparkline} height={56} />
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <Mini label="Generations" value={thisWeek?.generations ?? 0} />
        <Mini label="Documents"   value={thisWeek?.documents ?? 0} />
        <Mini label="Graded"      value={thisWeek?.assessments ?? 0} />
      </div>
      <p className="mt-2 text-[10px] uppercase tracking-[0.18em] font-medium text-muted-foreground text-center">
        Last 7 days
      </p>
    </div>
  );
}

function AISuggestionsCard({ suggestions, loading, onActionClick }) {
  return (
    <div
      className="col-span-12 sm:col-span-6 lg:col-span-4 tb-card p-6 tb-fade-up relative overflow-hidden"
      data-testid="ai-suggestions-card"
    >
      <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-primary inline-flex items-center gap-2">
        <Lightbulb className="h-3 w-3" /> AI suggestions
      </p>
      <h3 className="mt-2 font-display text-base font-medium">
        Smart next steps based on your last 14 days
      </h3>
      {loading ? (
        <div className="mt-4 space-y-2">
          <SkeletonRow lines={1} />
          <SkeletonRow lines={1} />
        </div>
      ) : suggestions.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Use TeeBee for a few days — I&apos;ll surface tailored shortcuts here.
        </p>
      ) : (
        <ul className="mt-4 space-y-2.5">
          {suggestions.map((s) => {
            const Icon = ICON_BY_KEY[s.icon] || Sparkles;
            return (
              <li key={s.id}>
                <button
                  onClick={() => onActionClick(s)}
                  className="w-full group flex items-start gap-3 rounded-xl p-3 -mx-1 hover:bg-primary/5 transition-colors text-left"
                  data-testid={`ai-suggestion-${s.id}`}
                >
                  <span className="tb-chip-lavender h-8 w-8 rounded-xl grid place-items-center shrink-0">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{s.label}</p>
                    <p className="text-[11px] text-muted-foreground line-clamp-1">{s.hint}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ActivityTimelineCard({ loading, items }) {
  return (
    <div
      className="col-span-12 lg:col-span-4 lg:row-span-2 tb-card p-6 tb-fade-up"
      data-testid="activity-timeline"
    >
      <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground inline-flex items-center gap-2">
        <CircleDot className="h-3 w-3" /> Activity
      </p>
      <h3 className="mt-2 font-display text-base font-medium">Last 14 days</h3>

      {loading ? (
        <div className="mt-4 space-y-3">
          {[1, 2, 3, 4].map((i) => <SkeletonRow key={i} lines={1} />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyBlock
          icon={CircleDot}
          title="Nothing yet"
          hint="Your AI activity timeline appears here."
          compact
        />
      ) : (
        <ol className="mt-5 relative border-l border-border/70 ml-1.5 space-y-4">
          {items.slice(0, 8).map((it, i) => {
            const meta = ACTIVITY_META[it.kind] || ACTIVITY_META.generation;
            const Icon = meta.icon;
            return (
              <li key={it.id || it.lesson_id || it.created_at || i} className="ml-5 relative">
                <span className={`absolute -left-[34px] top-0.5 h-7 w-7 rounded-full grid place-items-center ${meta.chip}`}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <p className="text-sm font-medium truncate">{it.title || "Untitled"}</p>
                <p className="text-[11px] text-muted-foreground capitalize">
                  {(it.label || it.kind || "").replace(/_/g, " ")} · {relativeTime(it.created_at)}
                </p>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function RecentOcrCard({ loading, items, onOpen, onItemClick }) {
  return (
    <div
      className="col-span-12 sm:col-span-6 lg:col-span-4 tb-card p-6 tb-fade-up"
      data-testid="recent-ocr-card"
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground inline-flex items-center gap-2">
          <ScanLine className="h-3 w-3" /> Recent OCR
        </p>
        <Button size="sm" variant="ghost" className="rounded-full" onClick={onOpen} data-testid="recent-ocr-open">
          Open <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
        </Button>
      </div>

      {loading ? (
        <div className="mt-4 space-y-3">
          {[1, 2, 3].map((i) => <SkeletonRow key={i} lines={1} />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyBlock
          icon={ScanLine}
          title="No documents yet"
          hint="Scan a notebook page to extract, summarize and turn it into a quiz."
          actionLabel="Try the Document OCR"
          onAction={onOpen}
          compact
        />
      ) : (
        <ul className="mt-4 space-y-2">
          {items.map((d) => (
            <li key={d.document_id}>
              <button
                onClick={onItemClick}
                className="w-full flex items-center gap-3 p-2.5 -mx-1 rounded-xl hover:bg-muted/60 transition text-left"
                data-testid={`recent-ocr-${d.document_id}`}
              >
                <span className="tb-chip-mint h-9 w-9 rounded-xl grid place-items-center shrink-0">
                  <ScanLine className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{d.title || "Untitled"}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {d.detected_language || "Unknown"} · {relativeTime(d.created_at)}
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecentLessonsCard({ loading, items, onOpen }) {
  return (
    <div
      className="col-span-12 lg:col-span-8 tb-card p-6 tb-fade-up"
      data-testid="recent-lessons-card"
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground inline-flex items-center gap-2">
          <PenLine className="h-3 w-3" /> Recent lessons
        </p>
        <Button size="sm" variant="ghost" className="rounded-full" onClick={onOpen} data-testid="recent-lessons-open">
          Open <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
        </Button>
      </div>

      {loading ? (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SkeletonRow /> <SkeletonRow /> <SkeletonRow /> <SkeletonRow />
        </div>
      ) : items.length === 0 ? (
        <EmptyBlock
          icon={PenLine}
          title="Your lessons will live here"
          hint="Generate a lesson plan to begin building your reusable library."
          actionLabel="Generate a lesson plan"
          onAction={onOpen}
        />
      ) : (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map((l) => (
            <div
              key={l.record_id || l.title}
              className="rounded-2xl border border-border/60 p-4 hover:border-primary/30 hover:-translate-y-0.5 transition-all cursor-pointer bg-card/60"
              onClick={onOpen}
              data-testid={`recent-lesson-${l.record_id || "item"}`}
            >
              <span className="tb-chip-lavender inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold rounded-full px-2 py-0.5">
                {(l.kind || "lesson").replace(/_/g, " ")}
              </span>
              <p className="mt-3 text-sm font-display font-medium tracking-tight leading-snug line-clamp-2">{l.title}</p>
              <p className="mt-2 text-[11px] text-muted-foreground">{relativeTime(l.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InstitutionNoticesCard({ loading, items, isInInstitution }) {
  return (
    <div
      className="col-span-12 sm:col-span-6 lg:col-span-4 tb-card p-6 tb-fade-up"
      data-testid="institution-notices"
    >
      <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground inline-flex items-center gap-2">
        <Building2 className="h-3 w-3" /> Institution notices
      </p>
      {!isInInstitution ? (
        <EmptyBlock
          icon={Building2}
          title="Join an institution"
          hint="Once you're part of a school, notices from admins appear here."
          compact
        />
      ) : loading ? (
        <div className="mt-4 space-y-3">
          <SkeletonRow lines={1} /> <SkeletonRow lines={1} />
        </div>
      ) : items.length === 0 ? (
        <EmptyBlock
          icon={Building2}
          title="All quiet"
          hint="No notices from your institution right now."
          compact
        />
      ) : (
        <ul className="mt-4 space-y-3">
          {items.map((n, i) => (
            <li key={n.notification_id || n.id || `${n.title}-${i}`} className="rounded-xl bg-muted/50 p-3 border border-border/50">
              <p className="text-sm font-medium">{n.title || n.subject}</p>
              <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{n.body || n.message}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatsRow({ stats, loading }) {
  const items = [
    { label: "Classes",      value: stats?.totals?.classes ?? 0,      chip: "tb-chip-mint" },
    { label: "Documents",    value: stats?.totals?.documents ?? 0,    chip: "tb-chip-butter" },
    { label: "Generations",  value: stats?.totals?.generations ?? 0,  chip: "tb-chip-lavender" },
    { label: "Assessments",  value: stats?.totals?.assessments ?? 0,  chip: "tb-chip-sky" },
  ];
  return (
    <div className="col-span-12 grid grid-cols-2 sm:grid-cols-4 gap-3 tb-fade-up" data-testid="stats-row">
      {items.map((it) => (
        <div key={it.label} className="tb-card p-4 sm:p-5">
          <p className={`inline-flex whitespace-nowrap text-[10px] uppercase tracking-[0.18em] font-semibold rounded-full px-2 py-0.5 ${it.chip}`}>
            {it.label}
          </p>
          {loading ? (
            <Skeleton className="mt-3 h-8 w-16" />
          ) : (
            <p className="mt-2 font-display text-2xl sm:text-3xl tracking-tight font-medium">{it.value}</p>
          )}
        </div>
      ))}
    </div>
  );
}

/* ----- utility leaf components ----- */

function Mini({ label, value }) {
  return (
    <div>
      <p className="font-display text-lg tracking-tight font-medium">{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em]">{label}</p>
    </div>
  );
}

function EmptyBlock({ icon: Icon = Sparkles, title, hint, actionLabel, onAction, compact = false }) {
  return (
    <div className={`grid place-items-center text-center ${compact ? "mt-4 py-6" : "mt-6 py-10"}`}>
      <span className="h-12 w-12 rounded-full border border-dashed border-border grid place-items-center mb-3 bg-muted/30">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </span>
      <p className="text-sm font-display font-medium">{title}</p>
      <p className="mt-1 text-[12px] text-muted-foreground max-w-[24ch]">{hint}</p>
      {actionLabel && onAction && (
        <Button size="sm" variant="outline" className="mt-3 rounded-full" onClick={onAction}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

/* ============================================================
   CareerIdentityRow — 4 elegant tiles linking to Resume / Career / Profile / Public Profile
   ============================================================ */
function CareerIdentityRow({ navigate }) {
  const tiles = [
    {
      key: "growth",
      label: "Growth Studio",
      hint: "Profile scores · branding · portfolio",
      icon: Sparkles,
      chip: "tb-chip-amber",
      to: "/growth-studio",
    },
    {
      key: "resume",
      label: "Build AI Resume",
      hint: "Premium educator CV in seconds",
      icon: FileText,
      chip: "tb-chip-lavender",
      to: "/resume",
    },
    {
      key: "career",
      label: "Career Copilot",
      hint: "Score · advice · interview prep",
      icon: Compass,
      chip: "tb-chip-sky",
      to: "/career",
    },
    {
      key: "portfolio",
      label: "Teaching Portfolio",
      hint: "Showcase your best work",
      icon: UserCog,
      chip: "tb-chip-mint",
      to: "/growth-studio/portfolio",
    },
    {
      key: "demo",
      label: "Demo Lesson Prep",
      hint: "5 / 10 / 20 min scripts",
      icon: Globe,
      chip: "tb-chip-butter",
      to: "/growth-studio/demo-lesson",
    },
    {
      key: "global",
      label: "Global Readiness",
      hint: "Unlock international roles",
      icon: Globe,
      chip: "tb-chip-sky",
      to: "/growth-studio/global-readiness",
    },
  ];
  return (
    <section className="mt-6" data-testid="career-identity-row">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground inline-flex items-center gap-2">
          <Sparkles className="h-3 w-3" /> Your educator identity & growth
        </p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 tb-stagger">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => navigate(t.to)}
              className="tb-card-interactive p-4 text-left group"
              data-testid={`identity-tile-${t.key}`}
            >
              <span className={`inline-grid place-items-center h-9 w-9 rounded-xl ${t.chip}`}>
                <Icon className="h-4 w-4" />
              </span>
              <p className="mt-3 text-sm font-display font-medium tracking-tight leading-snug">{t.label}</p>
              <p className="mt-1 text-[11px] text-muted-foreground line-clamp-2">{t.hint}</p>
              <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary">
                Open <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* ============================================================
   StreakStrip — daily-habit + viral-vanity bar under the hero greeting
   ============================================================ */
function StreakStrip({ streak, sharing, navigate, loading }) {
  if (loading || !streak) {
    return (
      <div className="relative mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="streak-strip-loading">
        {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }
  const goalPct = streak.weekly_progress_pct ?? 0;
  return (
    <div className="relative mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 tb-fade-up" data-testid="streak-strip">
      {/* Streak */}
      <button
        onClick={() => navigate("/preferences")}
        className={`tb-card-interactive p-4 text-left ${streak.at_risk ? "border-amber-300/60" : ""}`}
        data-testid="streak-card"
      >
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">
          <Flame className={`h-3 w-3 ${streak.streak_days > 0 ? "text-orange-500" : ""}`} />
          {streak.at_risk ? "Streak at risk" : "Current streak"}
        </div>
        <p className="mt-1.5 font-display text-2xl tracking-tight font-medium">
          {streak.streak_days || 0}<span className="text-muted-foreground text-base"> {streak.streak_days === 1 ? "day" : "days"}</span>
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {streak.active_today ? "Counted today" : streak.at_risk ? "Generate something today!" : `Longest: ${streak.longest_streak || 0}d`}
        </p>
      </button>

      {/* Weekly goal */}
      <button
        onClick={() => navigate("/preferences")}
        className="tb-card-interactive p-4 text-left"
        data-testid="weekly-goal-card"
      >
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">
          <Target className="h-3 w-3" /> Weekly goal
        </div>
        <p className="mt-1.5 font-display text-2xl tracking-tight font-medium">
          {streak.weekly_count}<span className="text-muted-foreground text-base"> / {streak.weekly_goal}</span>
        </p>
        <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${goalPct}%` }} />
        </div>
      </button>

      {/* Public lessons reach */}
      <button
        onClick={() => navigate("/lessons")}
        className="tb-card-interactive p-4 text-left"
        data-testid="reach-card"
      >
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">
          <Globe className="h-3 w-3" /> Your reach
        </div>
        <p className="mt-1.5 font-display text-2xl tracking-tight font-medium">
          {sharing?.total_views || 0}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5 inline-flex items-center gap-1">
          <Eye className="h-3 w-3" /> {sharing?.published_count || 0} lesson{sharing?.published_count === 1 ? "" : "s"} shared
        </p>
      </button>

      {/* Duplicates by other teachers */}
      <button
        onClick={() => navigate("/lessons")}
        className="tb-card-interactive p-4 text-left"
        data-testid="impact-card"
      >
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">
          <CopyIcon className="h-3 w-3" /> Impact
        </div>
        <p className="mt-1.5 font-display text-2xl tracking-tight font-medium">
          {sharing?.total_duplicates || 0}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">teachers used your lessons</p>
      </button>
    </div>
  );
}
